/**
 * Accounting API — főkönyvi napló (General Ledger / Journal).
 *
 * A 19-kör BE-1 (#597 `postFromInvoice`) számla→GL double-entry-t posztol; ez a read-oldal a
 * főkönyvi naplót (journal entries + lines) jeleníti meg a Pénzügy nézetben.
 *
 * Kontraktus (Zeno, 2026-06-22):
 *   GET /api/v1/accounting/journal?from=YYYY-MM-DD&to=YYYY-MM-DD[&account=&sourceType=]
 *   → { data: { entries: JournalEntry[] } }
 *
 * ⚠️ Pénz-pontosság: a Decimal mezők STRING-ként jönnek (`"12700.00"`) — NE float-olj számításhoz.
 * Ez a nézet read-only (a BE adja a totalDebit/Credit + line-számokat), így a stringet csak
 * megjelenítéskor (`Number()` → `Intl.NumberFormat`) konvertáljuk, aritmetika nélkül.
 */
import { api } from '@/api/client';

/** A főkönyvi tétel forrás-típusa (a #597 számla + #596 kaució + #600 kaució-kár double-entry flow-jai). */
export type JournalSourceType =
  | 'SALE'
  | 'INVOICE'
  | 'RENTAL_DEPOSIT'
  | 'RENTAL_DEPOSIT_REFUND'
  | 'RENTAL_DEPOSIT_FORFEIT';

/** Egy könyvelési sor (a kettős könyvvitel egy lába). Az összegek Decimal→string. */
export interface JournalLine {
  /** Főkönyvi számlaszám, pl. `"311"`. */
  accountCode: string;
  /** Számla megnevezése, pl. `"Vevők (követelés)"`. */
  accountName: string;
  /** Tartozik összeg (Decimal stringként), pl. `"12700.00"`. */
  debitAmount: string;
  /** Követel összeg (Decimal stringként), pl. `"0.00"`. */
  creditAmount: string;
}

/** Egy főkönyvi tétel (journal entry) a hozzá tartozó sorokkal. */
export interface JournalEntry {
  id: string;
  /** Emberi olvasású tételszám, pl. `"JE-SZLA-2026-0001"`. */
  entryNumber: string;
  /** Könyvelési dátum, `YYYY-MM-DD`. */
  entryDate: string;
  description: string;
  sourceType: JournalSourceType;
  sourceId: string;
  /** Tartozik-oldal összesen (Decimal string) — balansz: == totalCredit. */
  totalDebit: string;
  /** Követel-oldal összesen (Decimal string). */
  totalCredit: string;
  lines: JournalLine[];
}

/** A `getJournalEntries` lekérdezés paraméterei. */
export interface JournalQueryParams {
  /** Tól-dátum (`YYYY-MM-DD`), inkluzív. */
  from: string;
  /** Ig-dátum (`YYYY-MM-DD`), inkluzív. */
  to: string;
  /** Opcionális: szűrés egy főkönyvi számlaszámra. */
  account?: string | undefined;
  /** Opcionális: szűrés forrás-típusra. */
  sourceType?: JournalSourceType | undefined;
}

/** A journal endpoint nyers válasz-burka (`{ data: { entries } }`, KGC API-konvenció). */
interface JournalResponse {
  data: { entries: JournalEntry[] };
}

/**
 * Lekéri a főkönyvi napló tételeit a megadott dátum-intervallumra (opc. számla- / forrás-szűrővel).
 * A `{ data: { entries } }` burkot kicsomagolja → közvetlenül a tétel-tömböt adja vissza.
 */
export async function getJournalEntries(params: JournalQueryParams): Promise<JournalEntry[]> {
  const qs = new URLSearchParams({ from: params.from, to: params.to });
  if (params.account) qs.set('account', params.account);
  if (params.sourceType) qs.set('sourceType', params.sourceType);

  const res = await api.get<JournalResponse>(`/accounting/journal?${qs.toString()}`);
  return res.data.entries;
}
